/**
 * An entry is one moment in time carrying any number of measurements.
 *
 * Per ADR-0005 the note and tags belong to the **entry**, not to individual
 * values, because they describe the moment rather than any single number. The
 * rejected alternative — one flat row per measurement — cannot tell a
 * deliberate multi-track check-in from several coincidental logs at the same
 * second, and forces a shared note to be duplicated onto every row.
 */

import { err, invalid, ok, type Result, type ValidationError } from './result.js'
import { isSafeColour } from './colour.js'
import type { Clock } from '../ports/clock.js'
import type { Timestamp } from './timestamp.js'
import { validateTrackValue, type Track, type TrackValue } from './track.js'

export interface Measurement {
  readonly trackId: string
  readonly value: TrackValue
  /** For the rare case where one specific value needs explaining. */
  readonly note?: string
}

export interface Tag {
  readonly id: string
  readonly name: string
  readonly colour?: string
}

export interface Entry {
  readonly id: string
  /** When it happened, in the zone it happened in (ADR-0006). */
  readonly at: Timestamp
  readonly note?: string
  readonly tagIds: readonly string[]
  readonly measurements: readonly Measurement[]
  /**
   * Which check-in produced this, where one did.
   *
   * Kept so a report can compare like with like rather than averaging across
   * contexts that are not comparable. It costs one field and cannot be
   * recovered retrospectively, which is why it is here from the start.
   */
  readonly templateId?: string
  readonly createdAt: Timestamp
  readonly updatedAt: Timestamp
}

const MAX_NOTE_LENGTH = 2_000
const MAX_TAG_NAME_LENGTH = 40

export interface EntryContext {
  /** Every track the entry might reference, archived ones included. */
  readonly tracks: readonly Track[]
  readonly tags?: readonly Tag[]
}

export function validateTag(tag: Tag, existing: readonly Tag[] = []): Result<Tag> {
  const errors: ValidationError[] = []
  const name = tag.name.trim()

  if (name === '') {
    errors.push(invalid('tag.name.empty', 'A tag needs a name.', 'name'))
  } else if (name.length > MAX_TAG_NAME_LENGTH) {
    errors.push(
      invalid(
        'tag.name.tooLong',
        `A tag name can be at most ${String(MAX_TAG_NAME_LENGTH)} characters.`,
        'name',
      ),
    )
  }

  /*
   * A colour is the one field here that stops being text: it is put straight
   * into a `background`, and CSS values have side effects — `url(https://…)`
   * fetches when it paints, from an app whose promise is that nothing leaves
   * the device. See `colour.ts`.
   */
  if (tag.colour !== undefined && !isSafeColour(tag.colour)) {
    errors.push(
      invalid(
        'tag.colour.invalid',
        'A tag colour has to be a hex colour, like #8b939e.',
        'colour',
      ),
    )
  }

  const clash = existing.some(
    (other) =>
      other.id !== tag.id && other.name.trim().toLowerCase() === name.toLowerCase(),
  )
  if (name !== '' && clash) {
    errors.push(
      invalid('tag.name.duplicate', `You already have a tag called "${name}".`, 'name'),
    )
  }

  return errors.length > 0 ? err<Tag>(...errors) : ok(tag)
}

export function validateEntry(entry: Entry, context: EntryContext): Result<Entry> {
  const errors: ValidationError[] = []
  const tracksById = new Map(context.tracks.map((track) => [track.id, track]))
  const tagIds = new Set((context.tags ?? []).map((tag) => tag.id))

  if (entry.measurements.length === 0) {
    errors.push(
      invalid(
        'entry.measurements.empty',
        'An entry needs at least one measurement.',
        'measurements',
      ),
    )
  }

  const seenTrackIds = new Set<string>()
  for (const measurement of entry.measurements) {
    if (seenTrackIds.has(measurement.trackId)) {
      errors.push(
        invalid(
          'entry.measurements.duplicateTrack',
          'The same track is measured twice in this entry.',
          'measurements',
        ),
      )
      continue
    }
    seenTrackIds.add(measurement.trackId)

    const track = tracksById.get(measurement.trackId)
    if (track === undefined) {
      errors.push(
        invalid(
          'entry.measurement.unknownTrack',
          'This entry refers to a track that no longer exists.',
          'measurements',
        ),
      )
      continue
    }

    // Deliberately no archived check. Archiving a track must never invalidate
    // the history recorded against it (ADR-0003) — history is the whole point.
    const valueResult = validateTrackValue(track, measurement.value)
    if (!valueResult.ok) {
      errors.push(...valueResult.errors)
    }

    if (measurement.note !== undefined && measurement.note.length > MAX_NOTE_LENGTH) {
      errors.push(
        invalid(
          'entry.measurement.note.tooLong',
          `A note can be at most ${String(MAX_NOTE_LENGTH)} characters.`,
          'measurements',
        ),
      )
    }
  }

  if (entry.note !== undefined && entry.note.length > MAX_NOTE_LENGTH) {
    errors.push(
      invalid(
        'entry.note.tooLong',
        `A note can be at most ${String(MAX_NOTE_LENGTH)} characters.`,
        'note',
      ),
    )
  }

  const seenTags = new Set<string>()
  for (const tagId of entry.tagIds) {
    if (seenTags.has(tagId)) {
      errors.push(
        invalid('entry.tags.duplicate', 'The same tag is applied twice.', 'tagIds'),
      )
    }
    seenTags.add(tagId)

    if (context.tags !== undefined && !tagIds.has(tagId)) {
      errors.push(
        invalid(
          'entry.tag.unknown',
          'This entry refers to a tag that no longer exists.',
          'tagIds',
        ),
      )
    }
  }

  return errors.length > 0 ? err<Entry>(...errors) : ok(entry)
}

export interface NewEntry {
  readonly id: string
  readonly measurements: readonly Measurement[]
  /** Omit to stamp the current moment; supply to backdate. */
  readonly at?: Timestamp
  readonly note?: string
  readonly tagIds?: readonly string[]
  readonly templateId?: string
}

/**
 * Builds an entry, stamping the current moment unless one is supplied.
 *
 * Backdating is ordinary use — logging what happened two hours ago — so an
 * explicit `at` is honoured exactly and never quietly replaced with now.
 */
export function makeEntry(input: NewEntry, clock: Clock): Entry {
  const now = clock.now()
  return {
    id: input.id,
    at: input.at ?? now,
    ...(input.note === undefined ? {} : { note: input.note }),
    tagIds: input.tagIds ?? [],
    measurements: input.measurements,
    ...(input.templateId === undefined ? {} : { templateId: input.templateId }),
    createdAt: now,
    updatedAt: now,
  }
}

/**
 * An edit, where an explicit `undefined` means **clear this field**.
 *
 * `Partial<Entry>` cannot say that under `exactOptionalPropertyTypes`: the bare
 * optional form rejects an explicit `undefined`, so there would be no way to
 * remove a note once one had been written. Same shape and same reason as
 * `TrackChanges`.
 */
export type EntryChanges = {
  readonly [K in keyof Omit<Entry, 'id' | 'createdAt'>]?: Entry[K] | undefined
}

/** Applies changes, moving `updatedAt` but never `createdAt`. */
export function reviseEntry(entry: Entry, changes: EntryChanges, clock: Clock): Entry {
  const merged: Record<string, unknown> = { ...entry }
  for (const [key, value] of Object.entries(changes)) {
    if (value === undefined) {
      // Deleted rather than stored: a present-but-undefined `note` round-trips
      // through IndexedDB and returns as a field that exists and holds nothing.
      delete merged[key]
    } else {
      merged[key] = value
    }
  }

  return {
    ...merged,
    id: entry.id,
    createdAt: entry.createdAt,
    updatedAt: clock.now(),
  } as Entry
}

/** The measurement for a track, if this entry recorded one. */
export function measurementFor(entry: Entry, trackId: string): Measurement | undefined {
  return entry.measurements.find((measurement) => measurement.trackId === trackId)
}

/**
 * Whether a track was measured in this entry.
 *
 * A skipped track records **no measurement**, never a zero or a default. A
 * missing reading and a reading of zero are entirely different claims, and
 * conflating them corrupts every average downstream.
 */
export function wasMeasured(entry: Entry, trackId: string): boolean {
  return measurementFor(entry, trackId) !== undefined
}

/** Every track id this entry touches, in the order recorded. */
export function measuredTrackIds(entry: Entry): string[] {
  return entry.measurements.map((measurement) => measurement.trackId)
}
